import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronLeft, Plus } from "lucide-react";

import { AppLogo } from "@/shared/components/AppLogo";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import { SearchInput } from "@/shared/components/ui-kit";
import { useApps, useConnections, useLinkConnection, useSetAppSource } from "@/shared/api/apps";
import { WhoseAccount } from "./WhoseAccount";

import type { AppConnection, AppRow } from "@/shared/types";

/* "Connect app" in the agent's Settings (CONTRACT §6, §7).

   Two ways in, because an account and an app are not the same thing. An account
   you have already authorized is one click — the agent link is all that is
   missing. An app you have never connected has to go through the provider's own
   consent page first, and only then can it be linked.

   So the dialog lists both: your accounts at the top, then the catalog. The
   catalog is 1,500 rows, so the search is the server's (`GET /apps?q=`) and the
   list grows a page at a time — nothing here filters catalog rows client-side.
   Accounts are a handful and are filtered here, against the same query.

   Whose account is asked once the app is picked (`WhoseAccount`): yours (every
   customer acts through it), or each customer's own — a GitHub reviewer works on
   each customer's repos, and they connect it on the agent's client app when it
   first needs it. An app that needs no account is added with none, no question. */
const PAGE = 40;
const DEBOUNCE_MS = 250;

/** An app whose `auth` is `none` needs no account: it is added with none. */
const needsNone = (app: AppRow) => app.auth === "none";

function Row({ logo, name, detail, action, onClick, disabled }: {
  logo: string | null;
  name: string;
  detail: string;
  action: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center gap-2.5 px-3 py-2 text-left min-w-0 hover:bg-[var(--surface-sunk)]/45 disabled:hover:bg-transparent disabled:cursor-default"
    >
      <AppLogo logo={logo} name={name} size={22} iconSize={13} rounded={6} />
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-medium tracking-[-0.005em] truncate text-[var(--text-primary)]">
          {name}
        </span>
        <span className="block text-[11.5px] text-[var(--text-tertiary)] truncate mt-[1px]">{detail}</span>
      </span>
      <span className="shrink-0 text-[11.5px] font-medium text-[var(--text-tertiary)]">{action}</span>
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-3 pb-1.5 section-label">
      {children}
    </div>
  );
}

export function ConnectAppDialog({ agentId, linked, added, onOpenChange }: {
  agentId: string;
  /** Connection ids already on this agent — those rows are "Added", not offers. */
  linked: string[];
  /** Slugs of apps already on the agent without a connection of yours. */
  added: string[];
  onOpenChange: (open: boolean) => void;
}) {
  const [picked, setPicked] = useState<AppRow | null>(null);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [size, setSize] = useState(PAGE);

  // Every keystroke would otherwise be a request against 1,500 rows, and the
  // sixth would still be in flight when the first one's answer landed.
  useEffect(() => {
    const timer = setTimeout(() => setQ(search.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => setSize(PAGE), [q]);

  const { data: page, isPending: appsPending } = useApps({ q: q || undefined, limit: size });
  const { data: connections } = useConnections();
  const link = useLinkConnection();
  const setSource = useSetAppSource();

  const accounts = useMemo(() => {
    const needle = q.toLowerCase();
    return (connections ?? []).filter(
      (c) =>
        c.status === "connected" &&
        !linked.includes(c.id) &&
        (!needle || `${c.app.name} ${c.account_label ?? ""}`.toLowerCase().includes(needle)),
    );
  }, [connections, linked, q]);

  const apps = page?.items ?? [];
  const remaining = Math.max((page?.total ?? 0) - (page?.items.length ?? 0), 0);
  const onAgent = (slug: string) =>
    added.includes(slug) || (connections ?? []).some((c) => c.app.slug === slug && linked.includes(c.id));

  const add = (connection: AppConnection) => link.mutate({ id: connection.id, body: { agent_id: agentId } });

  const choose = (app: AppRow) =>
    needsNone(app) ? setSource.mutate({ slug: app.slug, agentId, account: "none" }) : setPicked(app);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 w-[460px] max-w-[94vw] rounded-[16px] overflow-hidden bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog">
        {picked ? (
          <div className="px-5 pt-5 pb-5 space-y-3">
            <div>
              <button
                type="button"
                onClick={() => setPicked(null)}
                className="press -ml-1 mb-2 inline-flex items-center gap-0.5 rounded-[6px] px-1 py-0.5 text-[12px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              >
                <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.75} /> All apps
              </button>
              <DialogTitle className="flex items-center gap-2 text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
                <AppLogo logo={picked.logo} name={picked.name} size={22} iconSize={13} rounded={6} />
                Whose {picked.name} account?
              </DialogTitle>
              <DialogDescription className="text-[13px] text-[var(--text-tertiary)] mt-0.5">
                You can switch this later from the app's menu.
              </DialogDescription>
            </div>
            <WhoseAccount agentId={agentId} app={picked} onDone={() => setPicked(null)} />
          </div>
        ) : (
          <>
            <div className="px-5 pt-5 pb-3">
              <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
                Connect app
              </DialogTitle>
              <DialogDescription className="text-[13px] text-[var(--text-tertiary)] mt-0.5">
                Add an account you have already connected, or pick an app and then whose account it uses: yours, or each customer's own.
              </DialogDescription>
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search apps"
                className="mt-3 w-full"
                autoFocus
              />
            </div>

            <div className="mx-5 mb-5 rounded-[10px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] overflow-hidden">
              <div className="max-h-[360px] overflow-y-auto">
                {accounts.length > 0 && (
                  <>
                    <SectionLabel>Your accounts</SectionLabel>
                    <div className="divide-y divide-[var(--hairline)]">
                      {accounts.map((c) => (
                        <Row
                          key={c.id}
                          logo={c.app.logo}
                          name={c.app.name}
                          detail={c.account_label ?? (c.visibility === "org" ? "Shared with the workspace" : "Private to you")}
                          action={link.isPending ? "Adding…" : "Add"}
                          disabled={link.isPending}
                          onClick={() => add(c)}
                        />
                      ))}
                    </div>
                  </>
                )}

                <SectionLabel>{q ? "Matching apps" : "All apps"}</SectionLabel>
                <div className="divide-y divide-[var(--hairline)]">
                  {apps.map((app) => {
                    const already = onAgent(app.slug);
                    return (
                      <Row
                        key={app.slug}
                        logo={app.logo}
                        name={app.name}
                        detail={needsNone(app) ? "Needs no account" : app.description || app.category || `${app.tool_count} tools`}
                        disabled={already || setSource.isPending}
                        onClick={() => choose(app)}
                        action={already ? "Added" : <Plus className="w-3 h-3" strokeWidth={2} />}
                      />
                    );
                  })}
                </div>

                {!apps.length && (
                  <p className="m-0 px-3 py-6 text-center text-[12.5px] text-[var(--text-quaternary)]">
                    {appsPending ? "Loading apps…" : `No apps match “${q}”.`}
                  </p>
                )}

                {remaining > 0 && (
                  <button
                    onClick={() => setSize((n) => n + PAGE)}
                    className="press w-full h-8 flex items-center justify-center gap-1.5 border-t border-[var(--hairline)] text-[12.5px] font-medium text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
                  >
                    <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.75} />
                    Show {Math.min(PAGE, remaining)} more
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
